// PhotoGallery — reusable modal viewer for a position's photos (#1451).
// Shows one photo at a time with prev/next, a counter, close, keyboard
// arrows + Esc, and touch swipe. Built on the existing `Modal` (no new
// dependency). Used by both the SIMPLE editor strip and the read-only
// request view.
//
// @aiPurpose Page through an ORDERED (upload-order) list of a single
//   item's photos in a modal.
// @aiInvariant The ordered `keys` mixes persisted object keys and
//   current-session photos. A persisted key resolves to a signed
//   download URL ONLY when (a) a persisted request id and a persisted
//   `it-NNNN` item id exist and (b) the key matches the issued-photo
//   layout. Session photos pass their blob URL through `localUrls`; a
//   key failing both paths renders the placeholder — the download
//   endpoint is NEVER called for it.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton, Modal } from '../../../ui/index.js';
import { requestItemPhotoDownloadUrl } from '../../../api/requests/index.js';

// Mirrors the gate used by `DraftItemEditRow` / `ItemMobileCard`: only
// keys shaped like `request-item-photo/<uuid>/<uuid>` are eligible for a
// signed download-url POST.
const ISSUED_PHOTO_KEY_RE = /^request-item-photo\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/i;

function isPersistedItemId(id: string): boolean {
  return /^it-\d{4}$/.test(id);
}

export interface PhotoGalleryProps {
  open: boolean;
  onClose: () => void;
  /** Ordered (upload-order) object keys for the item being viewed. */
  keys: string[];
  /** Zero-based index the gallery opens at. */
  startIndex: number;
  /** Persisted request id, or `null` for an unsaved draft. */
  requestId: string | null;
  /** Item id — a server `it-NNNN` once persisted, else a draft id. */
  itemId: string;
  /**
   * Blob preview URLs for current-session photos, keyed by object key.
   * A key present here always renders from its local URL and never
   * triggers a download-url fetch.
   */
  localUrls?: Record<string, string>;
  // WHY (#2140): the archive snapshot view injects a snapshot-scoped photo
  //   resolver (live item keys 404 after a return). Passed as a prop — NOT
  //   read from the requests-feature context here — because `PhotoGallery`
  //   is the narrow `draft-media-capability` and must not depend on the
  //   feature. `ItemsTab` (a feature element) reads the context and forwards
  //   it. Absent/null → unchanged live behavior.
  resolver?: ((objectKey: string) => Promise<string | null>) | null;
}

export function PhotoGallery({
  open,
  onClose,
  keys,
  startIndex,
  requestId,
  itemId,
  localUrls,
  resolver = null,
}: PhotoGalleryProps) {
  const { t } = useTranslation(['requests', 'common']);
  const total = keys.length;
  const [index, setIndex] = useState(startIndex);
  // Re-clamp to the requested start photo each time the gallery opens.
  useEffect(() => {
    if (open) setIndex(clampIndex(startIndex, total));
  }, [open, startIndex, total]);

  const currentKey = keys[clampIndex(index, total)] ?? null;
  const localUrl = currentKey !== null ? (localUrls?.[currentKey] ?? null) : null;
  const canFetch =
    currentKey !== null &&
    localUrl === null &&
    ISSUED_PHOTO_KEY_RE.test(currentKey) &&
    (resolver !== null || (requestId !== null && isPersistedItemId(itemId)));

  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !canFetch || currentKey === null) {
      setDownloadUrl(null);
      return () => undefined;
    }
    let alive = true;
    setDownloadUrl(null);
    const p = resolver
      ? resolver(currentKey)
      : requestItemPhotoDownloadUrl(requestId as string, itemId, currentKey).then(
          (r) => r.downloadUrl,
        );
    p.then((url) => {
      if (alive) setDownloadUrl(url);
    }).catch(() => {
      // Silent fallback to the placeholder — same discipline as the
      // editor row and the mobile card thumbnail.
      if (alive) setDownloadUrl(null);
    });
    return () => {
      alive = false;
    };
  }, [open, canFetch, currentKey, requestId, itemId, resolver]);

  // Prev/next clamp at the ends (no wrap, per the UI contract).
  function go(delta: number): void {
    setIndex((i) => clampIndex(i + delta, total));
  }

  // Arrow-key navigation while open. Esc + the close icon are handled by
  // `Modal` itself.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, total]);

  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const displayUrl = localUrl ?? downloadUrl;
  const atStart = index <= 0;
  const atEnd = index >= total - 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('requests:photoGallery.counter', { current: total === 0 ? 0 : index + 1, total })}
      width={760}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          onTouchStart={(e) => setTouchStartX(e.touches[0]?.clientX ?? null)}
          onTouchEnd={(e) => {
            if (touchStartX === null) return;
            const endX = e.changedTouches[0]?.clientX ?? touchStartX;
            const dx = endX - touchStartX;
            // 40px threshold filters out incidental taps. Swipe left → next.
            if (dx <= -40) go(1);
            else if (dx >= 40) go(-1);
            setTouchStartX(null);
          }}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'min(60vh, 480px)',
            background: 'var(--bg-2)',
            borderRadius: 'var(--r-1)',
          }}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt=""
              style={{
                maxWidth: '100%',
                maxHeight: 'min(60vh, 480px)',
                objectFit: 'contain',
                borderRadius: 'var(--r-1)',
              }}
            />
          ) : (
            <span style={{ color: 'var(--fg-4)' }}>
              <Icon name="image" size={32} />
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton
            icon="chevron-left"
            size={32}
            label={t('requests:photoGallery.prev')}
            onClick={() => go(-1)}
            disabled={atStart}
          />
          <span style={{ fontSize: 13, color: 'var(--fg-2)', fontVariantNumeric: 'tabular-nums' }}>
            {t('requests:photoGallery.counter', {
              current: total === 0 ? 0 : index + 1,
              total,
            })}
          </span>
          <IconButton
            icon="chevron-right"
            size={32}
            label={t('requests:photoGallery.next')}
            onClick={() => go(1)}
            disabled={atEnd}
          />
        </div>
      </div>
    </Modal>
  );
}

function clampIndex(i: number, total: number): number {
  if (total <= 0) return 0;
  if (i < 0) return 0;
  if (i > total - 1) return total - 1;
  return i;
}
